import {Button, Col, Drawer, Form, InputNumber, Row, Select} from "antd";
import React, {useEffect, useMemo, useState} from "react";
import BookCover from "@components/book-cover";
import {InfoIcon} from "@components/icons/Icons";
import useHasPermission from "@hooks/useHasPermission";
import {useWishlistUpdateIssueMutation} from "@mwServices/User";
import {showResponse} from "@services/Api";
import TextArea from "antd/es/input/TextArea";
import {showInfoModal} from "@lib/showModal";
import {useWindowSize} from "@components/window-size-provider";

const WishlistEditDrawerContent = ({issue, form, onClose}) => {
    const [callUpdate, updateResponse] = useWishlistUpdateIssueMutation();
    const initForm = useMemo(() => ({
        wishlist_id: issue.wishlist_id,
        grade: issue.grade ? issue.grade : "0",
        graded_by: issue.graded_by ? issue.graded_by : 'RAW',
        wishlist_qty: issue.wishlist_qty ? issue.wishlist_qty : 1,
        notes: issue.notes,
    }), [issue]);

    const submitUpdates = async (values) => {
        let sanitisedData = {
            wishlist_id: issue.wishlist_id,
            grade: issue.grade,
            graded_by: issue.graded_by,
            wishlist_qty: issue.wishlist_qty,
            notes: issue.notes,
        };

        sanitisedData = {...sanitisedData, ...values};

        await callUpdate(sanitisedData);

        form.resetFields();
        onClose();
    };

    useEffect(() => {
        showResponse(updateResponse, 'Wishlist item updated.', 'Failed to update the wishlist item.');
    }, [updateResponse]);

    useEffect(() => {
        form.resetFields();
    }, [issue]);

    return (
        <>
            <Row>
                <Col span={24} className="flex-align-right">
                    <Button
                        className={'btn-price-guide'}
                        type="link"
                        href={`/issue/${issue.slug}`}
                    >Go To Price Guide &gt;</Button>
                </Col>
            </Row>
            <Row>
                <Col span={5}>
                    <BookCover issue={issue}size={'small'}  />
                </Col>
                <Col span={19} className="title-block">
                    <div>
                        <b>{issue.series_title}: {issue.issue_number}</b><br/>
                        {issue.is_variant ? issue.variant_name : 'Regular'}
                    </div>
                </Col>
            </Row>

            <Form
                labelCol={{span: 8}}
                initialValues={initForm}
                wrapperCol={{span: 16}}
                form={form}
                onFinish={submitUpdates}
            >
            <Row className="content-block">
                <Col span={24}>
                    <Row>
                        <Col span={6} className='align-left-top'>
                            <Button onClick={() =>{
                                    showInfoModal({
                                        title: "Desired Grade",
                                        content: (
                                            <>
                                                <p>The options presented here allow you to identify your comic as either Raw (i.e, uncertified / not graded) or Slabbed (i.e., certified / graded). In addition, you can select a grade that you desire.</p>
                                                <p>COVRPRICE takes into consideration the grade the comic is in and if it’s Raw or Slabbed when determining that most accurate FMV (Fair Market Value) to present for the comic. That said, it’s important to add this information for each of your wishlist comics.</p>
                                                <p>NOTE: If we don’t have sales data for the comic in the specific grade you’ve indicated, COVRPRICE will assign that comic the value of the “most common grade for that comic in our database. For example, if you indicate your comic is a 9.9 but there are no sales at that grade, but we have the most sales collected for a 9.6, you comic will show the FMV for the 9.6. Similarly, if you don’t add any grade information, we’ll assign that comic the value of the most common grade.</p>
                                            </>
                                        ),
                                    });
                                }} type="link" icon={<InfoIcon className="iconLarge"/>}/>
                        </Col>
                        <Col span={18}>

                            <Form.Item label="Quantity" name="wishlist_qty">
                                <InputNumber className='width-100' min={0} precision={0}/>
                            </Form.Item>

                            <Form.Item label="Raw / Slabbed" name="graded_by">
                                <Select>
                                    <Select.Option value="RAW">RAW</Select.Option>
                                    <Select.Option value="CGC">CGC</Select.Option>
                                    <Select.Option value="CBCS">CBCS</Select.Option>
                                    <Select.Option value="PSA">PSA</Select.Option>
                                    <Select.Option value="PGX">PGX</Select.Option>
                                    <Select.Option value="EGS">EGS</Select.Option>
                                    <Select.Option value="BECKETT">BECKETT</Select.Option>
                                    <Select.Option value="HALO">HALO</Select.Option>
                                    <Select.Option value="OTHER">OTHER</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Grade" name="grade">
                                <Select>
                                    <Select.Option value="0">No Rating</Select.Option>
                                    <Select.Option value="10.0">10.0 - Gem Mint</Select.Option>
                                    <Select.Option value="9.9">9.9 - Mint</Select.Option>
                                    <Select.Option value="9.8">9.8 - Near Mint / Mint</Select.Option>
                                    <Select.Option value="9.6">9.6 - Near Mint+</Select.Option>
                                    <Select.Option value="9.4">9.4 - Near Mint</Select.Option>
                                    <Select.Option value="9.2">9.2 - Near Mint-</Select.Option>
                                    <Select.Option value="9.0">9.0 - Very Fine / Near Mint</Select.Option>
                                    <Select.Option value="8.5">8.5 - Very Fine+</Select.Option>
                                    <Select.Option value="8.0">8.0 - Very Fine</Select.Option>
                                    <Select.Option value="7.5">7.5 - Very Fine-</Select.Option>
                                    <Select.Option value="7.0">7.0 - Fine / Very Fine</Select.Option>
                                    <Select.Option value="6.5">6.5 - Fine+</Select.Option>
                                    <Select.Option value="6.0">6.0 - Fine</Select.Option>
                                    <Select.Option value="5.5">5.5 - Fine-</Select.Option>
                                    <Select.Option value="5.0">5.0 - Very Good / Fine</Select.Option>
                                    <Select.Option value="4.5">4.5 - Very Good+</Select.Option>
                                    <Select.Option value="4.0">4.0 - Very Good</Select.Option>
                                    <Select.Option value="3.5">3.5 - Very Good-</Select.Option>
                                    <Select.Option value="3.0">3.0 - Good / Very Good</Select.Option>
                                    <Select.Option value="2.5">2.5 - Good+</Select.Option>
                                    <Select.Option value="2.0">2.0 - Good</Select.Option>
                                    <Select.Option value="1.8">1.8 - Good-</Select.Option>
                                    <Select.Option value="1.5">1.5 - Fair/Good</Select.Option>
                                    <Select.Option value="1.0">1.0 - Fair</Select.Option>
                                    <Select.Option value="0.5">0.5 - Poor</Select.Option>
                                    <Select.Option value="0.3">0.3 - Coverless</Select.Option>
                                </Select>
                            </Form.Item>

                            <Form.Item label="Notes" name="notes">
                                <TextArea rows={4} placeholder="" maxLength={1000} showCount/>
                            </Form.Item>
                        </Col>
                    </Row>
                </Col>
            </Row>
        </Form>
        </>
    );
};

const WishlistEditDrawer = ({issue, visible, onClose}) => {
    const [form] = Form.useForm();
    const windowDimensions = useWindowSize();
    const [drawerWidth, setDrawerWidth] = useState(450);
    const isAdmin = useHasPermission('admin');

    useEffect(() => {
        const newWidth = (windowDimensions.width < 450) ? windowDimensions.width : 450;
        if(newWidth !== drawerWidth) setDrawerWidth(newWidth);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowDimensions]);

    const onCloseExec = (event) => {
        if(event &&
            event.target &&
            event.target.className &&
            event.target.className.indexOf &&
            event.target.className.indexOf('ant-drawer-mask') !== -1){
            //click outside
            if(form.isFieldsTouched()){
                form.submit();
            } else {
                //No changes, close no save.
                form.resetFields();
                onClose();
            }
        } else {
            //click close button or other close event.
            form.resetFields();
            onClose();
        }
    };

    return (
        <Drawer
            title="Edit Wishlist Issue"
            className={"wishlist-edit-drawer"}
            headerStyle={{paddingTop: isAdmin ? 50 : 16}}
            width={drawerWidth}
            placement="right"
            onClose={onCloseExec}
            open={visible}
            footer={
                <Row justify="center">
                    <Col >
                        <Button className="outlineButton thickButton width-250px" onClick={() => form.submit()}>SAVE</Button>
                    </Col>
                </Row>
            }
        >
            {issue && (
                <WishlistEditDrawerContent issue={issue} onClose={onCloseExec} form={form} />
            )}
        </Drawer>
    );
};

export default WishlistEditDrawer;